import { useSearch } from "wouter"
import cx from "clsx"
import { MicrosoftSignInButton } from "../../../components/MicrosoftSignInButton"
import { ButtonLink } from "../../../components/ButtonLink"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { SadPage } from "../../../components/SadPage"
import { isUnauthorized } from "@rawa/api-client/ApiResponseError"
import { useKioskDevice } from "../hooks/useKioskDevice"
import { useKioskIdleTimeout } from "../hooks/useKioskIdleTimeout"
import { paths } from "../../../paths"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./KioskLoginPage.module.scss"
import { SERVER_UNREACHABLE_MESSAGE } from "../../../components/serverUnreachable"

type DeviceState = "loading" | "enrolled" | "unenrolled" | "unreachable"

// The kiosk's own sign-in screen, reached two ways: a tablet with no usable
// credential, and a staff member tapping the welcome-screen cog to change the
// location. Those need different words — telling a working tablet it "needs to be
// set up" is simply false — so the page asks which it is rather than inferring it
// from the route it came from.
//
// The idle timeout is armed only once enrolled. A client can tap the cog by
// accident, and the tablet must not sit on a sign-in screen until someone
// notices. Arming it while unenrolled would instead loop: back to the welcome
// screen, bounced by the guard, and here again every 60 seconds.
export function KioskLoginPage() {
  // Asked of the device query the welcome screen's guard shares. Nothing is
  // decided from an earlier answer — a tablet revoked since would arm the idle
  // timeout below and loop — so a check in flight reads as loading, as it does
  // in the guard. A failure other than a 401 is reported by the query client.
  const { status, error, fetchStatus } = useKioskDevice("KioskLoginPage")
  const device: DeviceState =
    status === "pending" || fetchStatus === "fetching"
      ? "loading"
      : status === "success"
        ? "enrolled"
        : isUnauthorized(error)
          ? "unenrolled"
          : "unreachable"
  // The SSO callback sends a principal Entra authenticated but that is not
  // assigned to this application back here with a reason. Without it a refused
  // staff member cannot tell a permissions problem from a broken sign-in.
  const notAuthorized =
    new URLSearchParams(useSearch()).get("error") === "not_authorized"

  useKioskIdleTimeout(60_000, undefined, device === "enrolled")

  if (device === "loading") return null
  if (device === "unreachable")
    return <SadPage kioskMode lines={[SERVER_UNREACHABLE_MESSAGE]} />

  return (
    <div className={styles.page}>
      <h1 className={cx(typography.h2, styles.heading)}>
        {device === "enrolled"
          ? "Staff sign-in"
          : "This kiosk needs to be set up"}
      </h1>
      {notAuthorized && (
        <ErrorCallout>
          That account is not authorized to use this application. Ask an
          administrator if you need access.
        </ErrorCallout>
      )}
      <div className={styles.card}>
        <p className={cx(typography.bodyLarge, styles.body)}>
          {device === "enrolled"
            ? "Sign in to change the location this device is collecting feedback for."
            : "A staff member needs to sign in to set the location of this device before we can collect any feedback."}
        </p>
        <MicrosoftSignInButton
          href={`/api/auth/login?redirect=${encodeURIComponent(paths.kioskLocation)}`}
        />
      </div>
      {/* A client who tapped the cog by accident should not have to wait out the
          idle timeout to get back. Offered only when enrolled: an unenrolled
          device has no home screen to return to, and the welcome route would
          bounce straight back here. */}
      {device === "enrolled" && (
        <ButtonLink href={paths.kioskWelcome} className={styles.btnReturn}>
          Back to home screen
        </ButtonLink>
      )}
    </div>
  )
}
